import { useState, type AnimationEvent as ReactAnimationEvent, type FormEvent as ReactFormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Sun, Moon, ShieldCheck } from 'lucide-react';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { FormError } from '@/components/ui/form-error';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Footer from '@/components/common/Footer';
import { cn } from '@/lib/utils';
import { emailRule, loginPasswordRule, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField } from '@/lib/form-validation';
import { getSafeRedirectPath } from '@/lib/redirect';

const schema = z.object({
  email: emailRule,
  password: loginPasswordRule,
});

type FormData = z.infer<typeof schema>;

export default function PlatformLoginPage() {
  const { login } = usePlatformAuth();
  const { theme, toggleTheme } = useTheme();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // Browser autofill writes the input value directly to the DOM, bypassing
  // react-hook-form state. Chrome fires an `animationstart` named `-webkit-autofill`
  // (Firefox/Safari may not), so we also patch values from the DOM at submit time.
  const syncAutofilledValue = (event: ReactAnimationEvent<HTMLInputElement>) => {
    const animation = event.animationName;
    if (!/autofill|auto-?fill/i.test(animation)) return;
    const input = event.currentTarget;
    const name = input.name as keyof FormData;
    if (!(name in schema.shape)) return;
    setValue(name, input.value, { shouldValidate: false });
  };

  const handleFormSubmit = (event: ReactFormEvent<HTMLFormElement>) => {
    for (const name of Object.keys(schema.shape) as (keyof FormData)[]) {
      if (getValues(name)) continue;
      const input = document.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (input && input.value) {
        setValue(name, input.value, { shouldValidate: false });
      }
    }
    void handleSubmit(onSubmit, onInvalid)(event);
  };

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      addToast('Welcome back!', 'success');
      navigate(getSafeRedirectPath(searchParams.get('redirect'), '/super-admin'));
    } catch (err: any) {
      addToast(err.message || 'Login failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <button onClick={toggleTheme} className="login-theme-toggle" aria-label="Toggle theme">
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>
      <main className="login-main">
        <div className="login-card">
          <div className="login-brand">
            <ShieldCheck size={40} className="mx-auto mb-2" />
            <h1>LabourFlow Platform</h1>
            <p>Super Admin Console</p>
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Platform Sign In</CardTitle>
              <CardDescription>Super administrator credentials only</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@labourflow.dev"
                    autoComplete="email"
                    onAnimationStart={syncAutofilledValue}
                    {...register('email')}
                    aria-invalid={errors.email ? true : undefined}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    className={cn(errors.email && 'border-error')}
                  />
                  <FormError id="email-error" message={errors.email?.message} />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    onAnimationStart={syncAutofilledValue}
                    {...register('password')}
                    aria-invalid={errors.password ? true : undefined}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    className={cn(errors.password && 'border-error')}
                  />
                  <FormError id="password-error" message={errors.password?.message} />
                </div>
                <Button type="submit" className="w-full" loading={loading}>Sign In</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
